
import React, { useState } from 'react';
import { useApp } from '@/contexts/AppContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ChefHat, PlayCircle, CheckCircle, Timer } from 'lucide-react';
import { OrderItem } from '@/types';

const KitchenPage: React.FC = () => {
  const { kitchenOrders, updateOrderItemStatus } = useApp();
  
  const handleUpdateStatus = (orderId: string, itemId: string, status: OrderItem['status']) => {
    updateOrderItemStatus(orderId, itemId, status);
  };
  
  return (
    <div className="space-y-6">
      <div className="flex flex-col space-y-2">
        <h1 className="text-3xl font-bold tracking-tight">Cozinha</h1>
        <p className="text-muted-foreground">
          Gerencie os pedidos em preparação na cozinha.
        </p>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card className="col-span-1">
          <CardHeader>
            <CardTitle className="flex items-center">
              <Timer className="mr-2 h-5 w-5" />
              Pedidos Pendentes
            </CardTitle>
          </CardHeader>
          <CardContent>
            {kitchenOrders
              .filter(({ items }) => items.some(item => item.status === 'pending'))
              .map(({ order, items }) => (
                <div key={order.id} className="mb-6 border rounded-md overflow-hidden">
                  <div className="bg-muted p-3 flex justify-between items-center">
                    <div>
                      <h3 className="font-medium">Pedido #{order.id.slice(-4)}</h3>
                      <p className="text-sm text-muted-foreground">
                        Mesa {order.tableId}
                      </p>
                    </div>
                    <Badge variant="outline">
                      {items.filter(item => item.status === 'pending').length} itens
                    </Badge>
                  </div>
                  
                  <div className="divide-y">
                    {items
                      .filter(item => item.status === 'pending')
                      .map(item => (
                        <div 
                          key={item.id} 
                          className="p-3 flex justify-between items-center animate-fade-in"
                        >
                          <div>
                            <div className="flex items-center">
                              <span className="font-medium">{item.quantity}x</span>
                              <h4 className="font-medium ml-2">{item.product.name}</h4>
                            </div>
                            {item.notes && (
                              <p className="text-sm text-muted-foreground ml-5 mt-1">
                                Obs: {item.notes}
                              </p>
                            )}
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleUpdateStatus(order.id, item.id, 'preparing')}
                          >
                            <PlayCircle className="h-4 w-4 mr-1" />
                            Preparar
                          </Button>
                        </div>
                      ))}
                  </div>
                </div>
              ))}
              
            {!kitchenOrders.some(({ items }) => items.some(item => item.status === 'pending')) && (
              <div className="text-center py-10 text-muted-foreground">
                Não há pedidos pendentes no momento
              </div>
            )}
          </CardContent>
        </Card>
        
        <Card className="col-span-1">
          <CardHeader>
            <CardTitle className="flex items-center">
              <ChefHat className="mr-2 h-5 w-5" />
              Em Preparação
            </CardTitle>
          </CardHeader>
          <CardContent>
            {kitchenOrders
              .filter(({ items }) => items.some(item => item.status === 'preparing'))
              .map(({ order, items }) => (
                <div key={order.id} className="mb-6 border rounded-md overflow-hidden">
                  <div className="bg-muted p-3 flex justify-between items-center">
                    <div>
                      <h3 className="font-medium">Pedido #{order.id.slice(-4)}</h3>
                      <p className="text-sm text-muted-foreground">
                        Mesa {order.tableId}
                      </p>
                    </div>
                    <Badge variant="outline">
                      {items.filter(item => item.status === 'preparing').length} itens
                    </Badge>
                  </div>
                  
                  <div className="divide-y">
                    {items
                      .filter(item => item.status === 'preparing')
                      .map(item => (
                        <div 
                          key={item.id} 
                          className="p-3 flex justify-between items-center animate-fade-in"
                        >
                          <div>
                            <div className="flex items-center">
                              <span className="font-medium">{item.quantity}x</span>
                              <h4 className="font-medium ml-2">{item.product.name}</h4>
                            </div>
                            {item.notes && (
                              <p className="text-sm text-muted-foreground ml-5 mt-1">
                                Obs: {item.notes}
                              </p>
                            )}
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleUpdateStatus(order.id, item.id, 'ready')}
                          >
                            <CheckCircle className="h-4 w-4 mr-1" />
                            Pronto
                          </Button>
                        </div>
                      ))}
                  </div>
                </div>
              ))}
              
            {!kitchenOrders.some(({ items }) => items.some(item => item.status === 'preparing')) && (
              <div className="text-center py-10 text-muted-foreground">
                Não há pedidos em preparação no momento
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default KitchenPage;
